<template>
  <div class="creature-corral" id="creatureCorral" ref="corral"></div>
</template>

<script>
/**
 * 随机动物访客系统 - 优化版
 * - 24 种动物，每种 2-3 个表情变体
 * - 5 级稀有度：普通60% / 稀有25% / 珍贵10% / 史诗4% / 传说1%
 * - 稀有度特效：史诗有光晕、传说有粒子 + 彩虹边框
 * - 点击互动：动物会说一句话再消失
 */

// 动物库：每种有多个表情变体
const ANIMALS = [
  // 猫咪家族
  { name: '小橘猫', lines: [' /\\_/\\ ', '( ^.^ )', ' > ^ < '], say: '喵~' },
  { name: '暹罗猫', lines: [' /\\_/\\ ', '( o.o )', ' > ^ < '], say: '呼噜噜...' },
  { name: '奶牛猫', lines: [' /\\_/\\ ', '( -.- )', ' > ^ < '], say: '铲屎的！' },
  { name: '惊讶猫', lines: [' /\\_/\\ ', '( O.O )', ' > ^ < '], say: '哇！' },
  // 狗狗家族
  { name: '柴犬', lines: ['  __  ', "(o''o)", ' -||- '], say: '汪汪！' },
  { name: '哈士奇', lines: ['  __  ', '(o^ o)', ' -||- '], say: '嗷呜~' },
  { name: '柯基', lines: ['  __  ', '(o_o)', ' c_c '], say: '屁股摇摇' },
  // 萌系
  { name: '兔子', lines: [' (\\(\\ ', '( -.-)', 'o_(")(")'], say: '蹦蹦跳' },
  { name: '仓鼠', lines: ['(OvO)', '( . )', '(___)'], say: '吱吱~' },
  { name: '熊猫', lines: [' (o_o) ', '/(|_|)\\', '  " "  '], say: '竹子真香' },
  // 鸟类
  { name: '猫头鹰', lines: [' ,__, ', '(O,O)', '( - )'], say: '咕咕~' },
  { name: '小鸡', lines: ['  ,  ', ' (o)', '/{|}\\'], say: '叽叽叽' },
  { name: '企鹅', lines: ['  __  ', '(o_o)', '( v )'], say: '嘎嘎！' },
  // 水生
  { name: '青蛙', lines: ['  __  ', '(o_o)', '( - )'], say: '呱呱~' },
  { name: '小鱼', lines: ['  ><>', ' < o >', '  <>< '], say: '泡泡~' },
  { name: '章鱼', lines: [' (o_o) ', '/|||||\\', ' v v v '], say: '咕噜噜' },
  // 其他
  { name: '狐狸', lines: [' /\\_/\\ ', '(~o.o)', ' > ^ < '], say: '狡猾~' },
  { name: '小熊', lines: ['  ^ ^  ', ' (o.o) ', '  ( )  '], say: '蜂蜜！' },
  { name: '小猪', lines: ['  ^ ^  ', ' (oo) ', ' (__) '], say: '哼哼~' },
  { name: '小鹿', lines: [' \\|/ ', ' (o.o)', '  /\\  '], say: '咩~' },
  // 稀有动物
  { name: '独角兽', lines: ['  /\\  ', ' (o.o)', ' /||\\ '], say: '✨魔法✨', rare: true },
  { name: '小龙', lines: [' /\\_/ ', '( O )', '/\\/\\ '], say: '嗷呜~火！', rare: true },
  { name: '凤凰', lines: [' \\|/ ', '( o )', '/|\\ '], say: '涅槃~', rare: true },
  { name: '麒麟', lines: [' /\\/\\ ', '( O )', ' ||| '], say: '祥瑞~', rare: true }
]

const RARITY_TABLE = [
  { tier: 'common',    label: '🌿 普通', chance: 0.60, cls: 'common' },
  { tier: 'uncommon',  label: '🌱 稀有', chance: 0.25, cls: 'uncommon' },
  { tier: 'rare',      label: '💎 珍贵', chance: 0.10, cls: 'rare' },
  { tier: 'epic',      label: '✨ 史诗', chance: 0.04, cls: 'epic' },
  { tier: 'legendary', label: '🌟 传说', chance: 0.01, cls: 'legendary' }
]

// 动物台词
const CLICK_SAYS = [
  '你好呀~', '别戳我！', '今天真棒', '继续加油', '摸摸头~',
  '嘿嘿嘿', '哇哦！', '学习辛苦', '休息一下？', '真聪明！'
]

const FLOATS = ['float1', 'float2', 'float3', 'float4']
const POSITIONS = [
  { top: '80px', left: '20px' },
  { top: '80px', right: '20px' },
  { bottom: '80px', left: '20px' },
  { bottom: '80px', right: '20px' },
  { top: '40%', left: '10px' },
  { top: '40%', right: '10px' },
  { top: '60%', left: '30px' },
  { top: '60%', right: '30px' }
]

function rollRarity () {
  let r = Math.random()
  let c = 0
  for (const t of RARITY_TABLE) {
    c += t.chance
    if (r <= c) return t
  }
  return RARITY_TABLE[0]
}

function pickAnimal () {
  // 稀有动物只有高稀有度才会出现
  const rarity = rollRarity()
  const isRareRoll = rarity.tier === 'epic' || rarity.tier === 'legendary'
  const pool = ANIMALS.filter(a => isRareRoll || !a.rare)
  const animal = pool[Math.floor(Math.random() * pool.length)]
  return { animal, rarity }
}

function pickPos () { return POSITIONS[Math.floor(Math.random() * POSITIONS.length)] }
function pickSay (animal) {
  if (animal.say) return animal.say
  return CLICK_SAYS[Math.floor(Math.random() * CLICK_SAYS.length)]
}

export default {
  name: 'CreatureCorral',
  data () {
    return { creatureCount: 0, timer: null }
  },
  mounted () {
    // 初始生成 2-3 只
    const initCount = 2 + Math.floor(Math.random() * 2)
    for (let i = 0; i < initCount; i++) {
      setTimeout(() => this.spawnCreature(), i * 300)
    }
    // 每 30-60 秒换一只
    this.timer = setInterval(() => {
      this.despawnRandom()
      setTimeout(() => this.spawnCreature(), 800)
    }, 30000 + Math.random() * 30000)
  },
  beforeUnmount () {
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    spawnCreature () {
      const corral = this.$refs.corral
      if (!corral || this.creatureCount >= 4) return

      const { animal, rarity } = pickAnimal()
      const pos = pickPos()
      const float = FLOATS[Math.floor(Math.random() * FLOATS.length)]
      const say = pickSay(animal)

      // 外层：定位 + 浮动
      const wrapper = document.createElement('div')
      wrapper.className = 'creature-float'
      wrapper.style.cssText = Object.entries(pos).map(([k, v]) => k + ':' + v).join(';') +
        ';animation:' + float + ' ' + (2.5 + Math.random() * 1.5) + 's ease-in-out infinite'

      // 内层：卡片样式
      const el = document.createElement('div')
      el.className = 'creature ' + rarity.cls

      // 稀有度特效
      if (rarity.tier === 'epic') {
        wrapper.classList.add('has-glow')
      }
      if (rarity.tier === 'legendary') {
        wrapper.classList.add('has-particles')
        // 添加粒子
        for (let i = 0; i < 6; i++) {
          const p = document.createElement('span')
          p.className = 'particle'
          p.style.cssText = `--i:${i};--angle:${i * 60}deg`
          wrapper.appendChild(p)
        }
      }

      // 内容
      el.innerHTML =
        '<span class="animal-name">' + animal.name + '</span>' +
        animal.lines.join('<br>') +
        '<span class="rarity-tag">' + rarity.label + '</span>'
      el.title = animal.name + ' · ' + rarity.label

      // 点击互动
      const comp = this
      el.onclick = function (e) {
        e.stopPropagation()
        // 显示台词气泡
        const bubble = document.createElement('div')
        bubble.className = 'speech-bubble'
        bubble.textContent = say
        wrapper.appendChild(bubble)

        // 延迟消失
        setTimeout(() => {
          wrapper.classList.add('fading')
          setTimeout(() => { wrapper.remove(); comp.creatureCount-- }, 600)
        }, 800)
      }

      wrapper.appendChild(el)
      corral.appendChild(wrapper)
      this.creatureCount++
    },
    despawnRandom () {
      const floats = this.$refs.corral.querySelectorAll('.creature-float:not(.fading)')
      if (!floats.length) return
      const el = floats[Math.floor(Math.random() * floats.length)]
      el.classList.add('fading')
      const self = this
      setTimeout(() => { el.remove(); self.creatureCount-- }, 600)
    }
  }
}
</script>
